import { useMemo } from 'react';
import type { PermissionKey } from '@relay-towing/api';
import type { Member } from '../lib/driverAppTabs';
import { useDriverPermissions } from './useDriverPermissions';
import { useUserCompanies } from './useUserCompanies';

export interface MemberState extends Member {
  /** Whether they hold a Roles page checkbox. False until it is known. */
  can: (key: PermissionKey) => boolean;
  /** The selected company's membership: name, plan, role. */
  membership: ReturnType<typeof useUserCompanies>['companies'][number] | null;
  isLoading: boolean;
}

/**
 * The signed-in member in the selected company, as the mobile shell reads
 * them: office role, whether they drive, their checkboxes, and the
 * features the company has turned on. One hook so
 * every screen agrees, as useDriverTabs does for the old shell.
 */
export const useMember = (): MemberState => {
  const { companies, selectedCompanyId } = useUserCompanies();
  const { permissions, isLoading } = useDriverPermissions();
  const membership =
    companies.find((seat) => seat.company.id === selectedCompanyId) ?? null;

  return useMemo(
    () => ({
      officeRole: membership?.officeRole ?? null,
      drives: membership?.drives ?? false,
      permissions,
      features: membership?.company.enabledFeatures ?? [],
      can: (key: PermissionKey) => permissions.includes(key),
      membership,
      isLoading: !membership || isLoading,
    }),
    [membership, permissions, isLoading]
  );
};
